import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, BarElement } from 'chart.js';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

function Dashboard({ showToast }) {
  const { t } = useTranslation();
  const { customers: allCustomers, warnings: allWarnings, models, cases: allCases, clearSampleData } = useData();
  const { filterCustomersByBranch, filterWarningsByBranch, currentUser, canViewAllBranches } = useAuth();
  const navigate = useNavigate();

  const handleClearSampleData = () => {
    const confirmed = window.confirm(
      'Bạn có chắc muốn xóa dữ liệu mẫu?\n\nThao tác sẽ reset danh sách khách hàng/mô hình/cảnh báo về dữ liệu mặc định và xóa cases/rules đã tạo.'
    );

    if (!confirmed) return;

    clearSampleData();
    showToast && showToast('✅ Đã clear dữ liệu mẫu thành công!', 'success');
  };

  // Filter data by user's accessible branches
  const customers = filterCustomersByBranch(allCustomers);
  const warnings = filterWarningsByBranch(allWarnings, allCustomers);
  const cases = allCases.filter(c => {
    const customer = allCustomers.find(cust => cust.id === c.customerId);
    if (!customer) return false;
    return filterCustomersByBranch([customer]).length > 0;
  });

  // Calculate statistics from filtered data
  const highRiskCount = customers.filter(c => c.riskLevel === 'Cao').length;
  const mediumRiskCount = customers.filter(c => c.riskLevel === 'Trung bình').length;
  const lowRiskCount = customers.filter(c => c.riskLevel === 'Thấp').length;
  const activeModelsCount = models.filter(m => m.status === 'active').length;
  
  // Case statistics
  const totalCases = cases.length;
  const activeCases = cases.filter(c => c.status === 'in_progress').length;
  const closedCases = cases.filter(c => c.status === 'closed').length;
  const cbsCases = cases.filter(c => c.currentWorkflow === 'cbs').length;
  const capHighCases = cases.filter(c => c.currentWorkflow === 'cap_high').length;
  const capMedLowCases = cases.filter(c => c.currentWorkflow === 'cap_medium' || c.currentWorkflow === 'cap_low').length;
  const autoCases = cases.filter(c => c.isManual === false).length;
  const manualCases = cases.filter(c => c.isManual === true).length;

  // Get recent warnings (latest 5)
  const recentWarnings = warnings
    .sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
    .slice(0, 5);

  const riskDistributionData = {
    labels: ['Rủi ro cao', 'Rủi ro trung bình', 'Rủi ro thấp'],
    datasets: [{
      data: [highRiskCount, mediumRiskCount, lowRiskCount],
      backgroundColor: ['#dc3545', '#ffc107', '#17a2b8'],
      borderWidth: 0,
      hoverOffset: 10
    }]
  };

  // Generate trend data from recent months
  const trendData = {
    labels: ['T1', 'T2', 'T3', 'T4', 'T5', 'T6'],
    datasets: [
      {
        label: 'Cảnh báo phát hiện',
        data: [45, 52, 48, 65, 58, warnings.length],
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13, 110, 253, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'Cảnh báo đã xử lý',
        data: [38, 45, 42, 58, 52, warnings.filter(w => w.status === 'resolved').length],
        borderColor: '#28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  // Models by type distribution
  const modelsByTypeData = {
    labels: models.map(m => m.name),
    datasets: [{
      label: 'Số chỉ tiêu',
      data: models.map(m => m.indicators.length),
      backgroundColor: 'rgba(13, 110, 253, 0.6)',
      borderColor: '#0d6efd',
      borderWidth: 1
    }]
  };
  
  // Case workflow distribution
  const caseWorkflowData = {
    labels: ['CBS Workflow', 'CAP High Risk', 'CAP Medium/Low'],
    datasets: [{
      data: [cbsCases, capHighCases, capMedLowCases],
      backgroundColor: ['#0d6efd', '#dc3545', '#ffc107'],
      borderWidth: 0,
      hoverOffset: 10
    }]
  };
  
  // Case status distribution
  const caseStatusData = {
    labels: ['Đang xử lý', 'Đã đóng'],
    datasets: [{
      data: [activeCases, closedCases],
      backgroundColor: ['#17a2b8', '#28a745'],
      borderWidth: 0,
      hoverOffset: 10
    }]
  };
  
  // Case creation method
  const caseCreationData = {
    labels: ['Tự động (Filter Rules)', 'Thủ công'],
    datasets: [{
      data: [autoCases, manualCases],
      backgroundColor: ['#6610f2', '#fd7e14'],
      borderWidth: 0,
      hoverOffset: 10
    }]
  };

  const getRiskBadgeClass = (risk) => {
    if (risk === 'Cao' || risk === 'high') return 'danger';
    if (risk === 'Trung bình' || risk === 'medium') return 'warning';
    return 'info';
  };

  const handleCardClick = (path) => {
    navigate(path);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { 
        position: 'top',
        onClick: (e, legendItem, legend) => {
          // Toggle dataset visibility on legend click
          const index = legendItem.datasetIndex;
          const chart = legend.chart;
          const meta = chart.getDatasetMeta(index);
          meta.hidden = !meta.hidden;
          chart.update();
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += context.parsed.y || context.parsed;
            return label;
          }
        }
      }
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        // Handle click on chart elements
        showToast('Xem chi tiết dữ liệu', 'info');
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true
      }
    }
  };

  // Specific options for models bar chart with more space for labels
  const modelsChartOptions = {
    ...chartOptions,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false,
          padding: 30,
          font: {
            size: 10
          }
        }
      },
      y: {
        beginAtZero: true
      }
    }
  };

  const doughnutOptions = {
    ...chartOptions,
    cutout: '60%',
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const labels = ['Cao', 'Trung bình', 'Thấp'];
        navigate('/customers', { state: { filterRisk: labels[index] } });
      }
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="page-header-sticky">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-1">
              <i className="bi bi-speedometer2 me-2"></i>
              Dashboard - {t('dashboard.title')}
            </h2>
            <p className="text-muted mb-0">Tổng quan hệ thống cảnh báo sớm và phân tích rủi ro</p>
          </div>
          <div className="text-muted">
            <button
              className="btn btn-sm btn-outline-danger me-3"
              onClick={handleClearSampleData}
              title="Xóa dữ liệu mẫu và reset về mặc định"
            >
              <i className="bi bi-trash3 me-1"></i>
              Clear dữ liệu mẫu
            </button>
            <i className="bi bi-calendar3 me-1"></i>
            {new Date().toLocaleString('vi-VN')}
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-3">
          <div 
            className="card border-danger hover-lift" 
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('/customers')}
            title="Click để xem danh sách khách hàng rủi ro cao"
          >
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted mb-1">Rủi ro cao</p>
                  <h3 className="text-danger mb-0">{highRiskCount}</h3>
                </div>
                <div className="fs-1 text-danger">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                </div>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <i className="bi bi-people me-1"></i> 
                  {((highRiskCount / customers.length) * 100).toFixed(1)}% tổng số KH
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div 
            className="card border-warning hover-lift"
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('/customers')}
            title="Click để xem danh sách khách hàng rủi ro trung bình"
          >
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted mb-1">Rủi ro trung bình</p>
                  <h3 className="text-warning mb-0">{mediumRiskCount}</h3>
                </div>
                <div className="fs-1 text-warning">
                  <i className="bi bi-exclamation-circle-fill"></i>
                </div>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <i className="bi bi-people me-1"></i> 
                  {((mediumRiskCount / customers.length) * 100).toFixed(1)}% tổng số KH
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div 
            className="card border-success hover-lift"
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('/customers')}
            title="Click để xem danh sách khách hàng rủi ro thấp"
          >
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted mb-1">Rủi ro thấp</p>
                  <h3 className="text-info mb-0">{lowRiskCount}</h3>
                </div>
                <div className="fs-1 text-info">
                  <i className="bi bi-info-circle-fill"></i>
                </div>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <i className="bi bi-people me-1"></i> 
                  {((lowRiskCount / customers.length) * 100).toFixed(1)}% tổng số KH
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div 
            className="card border-primary hover-lift"
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('/models')}
            title="Click để xem danh sách mô hình"
          >
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted mb-1">Tổng mô hình</p>
                  <h3 className="text-primary mb-0">{activeModelsCount}</h3>
                </div>
                <div className="fs-1 text-primary">
                  <i className="bi bi-diagram-3-fill"></i>
                </div>
              </div>
              <div className="mt-2">
                <small className="text-success">
                  <i className="bi bi-check-circle"></i> {models.length} mô hình tổng
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Case Management Statistics */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-3">
          <div 
            className="card border-info hover-lift" 
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('/case-management')}
            title="Click để xem quản lý cases"
          >
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted mb-1">{t('dashboard.totalCases')}</p>
                  <h3 className="text-info mb-0">{totalCases}</h3>
                </div>
                <div className="fs-1 text-info">
                  <i className="bi bi-folder2-open"></i>
                </div>
              </div>
              <div className="mt-2">
                <small className="text-success">
                  <i className="bi bi-arrow-up-circle me-1"></i> 
                  {activeCases} {t('dashboard.activeCases')}
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div 
            className="card border-primary hover-lift"
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('/case-management')}
            title="Cases CBS Workflow"
          >
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted mb-1">CBS Workflow</p>
                  <h3 className="text-primary mb-0">{cbsCases}</h3>
                </div>
                <div className="fs-1 text-primary">
                  <i className="bi bi-diagram-3"></i>
                </div>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <i className="bi bi-percent me-1"></i> 
                  {totalCases > 0 ? ((cbsCases / totalCases) * 100).toFixed(1) : 0}% tổng cases
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div 
            className="card border-danger hover-lift"
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('/case-management')}
            title="Cases CAP High Risk"
          >
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted mb-1">CAP High Risk</p>
                  <h3 className="text-danger mb-0">{capHighCases}</h3>
                </div>
                <div className="fs-1 text-danger">
                  <i className="bi bi-shield-exclamation"></i>
                </div>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <i className="bi bi-percent me-1"></i> 
                  {totalCases > 0 ? ((capHighCases / totalCases) * 100).toFixed(1) : 0}% tổng cases
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div 
            className="card border-warning hover-lift"
            style={{ cursor: 'pointer' }}
            onClick={() => handleCardClick('/case-management')}
            title="Cases CAP Medium/Low"
          >
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="text-muted mb-1">CAP Med/Low</p>
                  <h3 className="text-warning mb-0">{capMedLowCases}</h3>
                </div>
                <div className="fs-1 text-warning">
                  <i className="bi bi-shield-check"></i>
                </div>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <i className="bi bi-percent me-1"></i> 
                  {totalCases > 0 ? ((capMedLowCases / totalCases) * 100).toFixed(1) : 0}% tổng cases
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-4">
          <div className="card animate-fade-in">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Phân bổ theo mức độ rủi ro</h5>
              <small className="text-muted">Click vào biểu đồ để xem</small>
            </div>
            <div className="card-body">
              <Doughnut data={riskDistributionData} options={doughnutOptions} />
              <div className="mt-3 text-center">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Tổng: {customers.length} khách hàng
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-8">
          <div className="card animate-fade-in">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Xu hướng cảnh báo 6 tháng gần đây</h5>
              <small className="text-muted">Hover để xem chi tiết</small>
            </div>
            <div className="card-body">
              <Line data={trendData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Models Distribution */}
      <div className="row g-3 mb-4">
        <div className="col-12">
          <div className="card animate-fade-in-up">
            <div className="card-header bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Số chỉ tiêu theo mô hình</h5>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => navigate('/models')}
              >
                Xem tất cả mô hình <i className="bi bi-arrow-right"></i>
              </button>
            </div>
            <div className="card-body" style={{height: '400px'}}>
              <Bar data={modelsByTypeData} options={modelsChartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Case Statistics Charts */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-4">
          <div className="card animate-fade-in">
            <div className="card-header bg-white">
              <h5 className="mb-0">{t('dashboard.caseByWorkflow')}</h5>
            </div>
            <div className="card-body">
              <Doughnut data={caseWorkflowData} options={doughnutOptions} />
              <div className="mt-3 text-center">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  Tổng {totalCases} cases đang được quản lý
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card animate-fade-in">
            <div className="card-header bg-white">
              <h5 className="mb-0">{t('dashboard.caseByStatus')}</h5>
            </div>
            <div className="card-body">
              <Doughnut data={caseStatusData} options={doughnutOptions} />
              <div className="mt-3 text-center">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  {activeCases} cases đang được xử lý
                </small>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card animate-fade-in">
            <div className="card-header bg-white">
              <h5 className="mb-0">Nguồn tạo Cases</h5>
            </div>
            <div className="card-body">
              <Doughnut data={caseCreationData} options={doughnutOptions} />
              <div className="mt-3 text-center">
                <small className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  {autoCases} tự động / {manualCases} thủ công
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Alerts Table */}
      <div className="card animate-fade-in-up">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-bell me-2"></i>
            {t('dashboard.recentActivities')}
          </h5>
          <Link to="/alerts" className="btn btn-sm btn-outline-primary">
            {t('dashboard.viewAllCases')} <i className="bi bi-arrow-right"></i>
          </Link>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Mã</th>
                  <th>Khách hàng</th>
                  <th>Loại cảnh báo</th>
                  <th>Mức độ</th>
                  <th>Điểm</th>
                  <th>Trạng thái</th>
                  <th>Thời gian</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {recentWarnings.length > 0 ? recentWarnings.map((warning, index) => (
                  <tr 
                    key={index}
                    className="transition-all"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/alerts')}
                  >
                    <td><code>{warning.code}</code></td>
                    <td>{warning.customerName}</td>
                    <td>{warning.alertType}</td>
                    <td>
                      <span className={`badge bg-${getRiskBadgeClass(warning.riskLevel)}`}>
                        {warning.riskLevel === 'high' ? 'Cao' : warning.riskLevel === 'medium' ? 'Trung bình' : 'Thấp'}
                      </span>
                    </td>
                    <td><strong>{warning.score}/{warning.maxScore}</strong></td>
                    <td>
                      <span className={`badge bg-${
                        warning.status === 'new' ? 'danger' :
                        warning.status === 'in_progress' ? 'warning' :
                        warning.status === 'resolved' ? 'success' : 'secondary'
                      }`}>
                        {warning.status === 'new' ? 'Mới' :
                         warning.status === 'in_progress' ? 'Đang xử lý' :
                         warning.status === 'resolved' ? 'Đã xử lý' : warning.status}
                      </span>
                    </td>
                    <td><small className="text-muted">{warning.createdDate}</small></td>
                    <td>
                      <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={(e) => { e.stopPropagation(); navigate('/alerts'); }}
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      <i className="bi bi-inbox" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                      <p className="mt-2 mb-0">Chưa có cảnh báo nào</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
